import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import {
  MapPin, CheckCircle, ArrowRight, Sparkles, Users, Clock,
  Shield, Star, ChevronDown, ChevronUp, Building2, Heart, Phone, Lock
} from 'lucide-react';
import { useAuth } from '../lib/auth';

// ─── Hero images ──────────────────────────────────────────────────────────────
const HERO_BG = 'https://images.unsplash.com/photo-1778752780886-b7a612163778?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80';
const MARRAKECH_IMG = 'https://images.unsplash.com/photo-1570133435529-62359fac701b?crop=entropy&cs=srgb&fm=jpg&w=800&q=80';
const AGADIR_IMG = 'https://images.pexels.com/photos/30557503/pexels-photo-30557503.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&dpr=1';

// ─── Counter animation ─────────────────────────────────────────────────────────
function AnimatedNumber({ target, suffix = '', prefix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const num = parseFloat(target);
        const duration = 1600;
        const steps = 50;
        const increment = num / steps;
        let current = 0;
        const timer = setInterval(() => {
          current = Math.min(current + increment, num);
          setCount(parseFloat(current.toFixed(1)));
          if (current >= num) clearInterval(timer);
        }, duration / steps);
      }
    }, { threshold: 0.5 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
}

// ─── FAQ accordion ─────────────────────────────────────────────────────────────
function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border/60 last:border-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-4 text-left font-body font-medium text-sm hover:text-accent transition-colors gap-4"
      >
        <span>{q}</span>
        {open ? <ChevronUp className="w-4 h-4 flex-shrink-0 text-accent" /> : <ChevronDown className="w-4 h-4 flex-shrink-0 text-muted-foreground" />}
      </button>
      {open && <p className="pb-4 text-sm text-muted-foreground font-body leading-relaxed">{a}</p>}
    </div>
  );
}

// ─── Step card ─────────────────────────────────────────────────────────────────
function Step({ n, title, desc, active }) {
  return (
    <div className={`relative flex gap-4 p-5 rounded-2xl transition-all ${active ? 'bg-primary text-primary-foreground shadow-lg' : 'bg-white border border-border/50'}`}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 font-body ${active ? 'bg-accent text-white' : 'bg-muted text-primary'}`}>
        {n}
      </div>
      <div>
        <p className={`font-heading font-semibold text-base mb-1 ${active ? 'text-white' : 'text-foreground'}`}>{title}</p>
        <p className={`text-sm leading-relaxed font-body ${active ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>{desc}</p>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const { user } = useAuth();
  const [faqItems, setFaqItems] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    api.get('/faq').then(r => setFaqItems(r.data.slice(0, 5))).catch(() => {});
    api.get('/metrics').then(r => {
      const m = {};
      r.data.forEach(item => { m[item.key] = item.value; });
      setMetrics(m);
    }).catch(() => {});

    const timer = setInterval(() => setActiveStep(s => (s + 1) % 4), 2800);
    return () => clearInterval(timer);
  }, []);

  const steps = [
    { title: 'Évaluer', desc: 'Analyse de vos besoins : autonomie, accessibilité, attentes de répit.' },
    { title: 'Construire', desc: 'Hébergement adapté, transport PMR, activités, bien-être — sur mesure.' },
    { title: 'Coordonner', desc: 'Un seul interlocuteur centralise tout, avant et pendant le séjour.' },
    { title: 'Accompagner', desc: 'Un référent local dédié gère chaque imprévu sur place.' },
  ];

  const painPoints = [
    { icon: '🚌', title: 'Transport introuvable', desc: 'Trouver un véhicule PMR adapté à la destination — complexe, coûteux, incertain.' },
    { icon: '🧠', title: 'Charge mentale', desc: 'Coordonner hébergement, soins, activités : une organisation à plein temps.' },
    { icon: '💶', title: 'Coût opaque', desc: 'Devis multiples, frais cachés — impossible d\'avoir une vision claire du budget total.' },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">

      {/* ═══════════════════ HERO ═══════════════════ */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Riad marocain" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#30251F]/90 via-[#30251F]/70 to-[#30251F]/30" />
        </div>

        {/* Floating destination badge */}
        <div className="absolute top-24 right-6 md:right-12 hidden md:flex flex-col gap-2 items-end z-10">
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-4 py-3 text-white text-right">
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-body mb-1">Destinations pilotes</p>
            <div className="flex items-center gap-2 justify-end">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
              <p className="text-sm font-heading font-medium">Marrakech · Agadir</p>
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20">
          <div className="max-w-2xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-accent/20 backdrop-blur-sm border border-accent/30 rounded-full px-4 py-1.5 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-accent" />
              <span className="text-xs text-white/90 font-body tracking-wide">Séjours de répit · France & Maroc</span>
            </div>

            {/* Headline */}
            <h1 className="font-heading font-light text-white leading-[1.08] mb-6">
              <span className="block text-5xl sm:text-6xl lg:text-7xl">L'aidant souffle.</span>
              <span className="block text-5xl sm:text-6xl lg:text-7xl text-secondary/90 italic">La personne aidée</span>
              <span className="block text-5xl sm:text-6xl lg:text-7xl">reste entourée.</span>
            </h1>

            {/* Sub */}
            <p className="text-base md:text-lg text-white/75 font-body leading-relaxed mb-10 max-w-xl">
              M-CareLink coordonne vos séjours de répit de A à Z — hébergement adapté PMR, transport, accompagnement humain.
              À <strong className="text-secondary/90 font-medium">Marrakech</strong> ou <strong className="text-secondary/90 font-medium">Agadir</strong>, partir sereinement est enfin possible.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3 mb-12">
              <Link to="/demander-un-sejour" data-testid="hero-cta-primary">
                <Button size="lg" className="rounded-full bg-accent hover:bg-accent/90 text-white font-body gap-2 px-7 h-13 shadow-lg shadow-accent/25 transition-all hover:scale-105">
                  Découvrir nos séjours <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/tester-mon-eligibilite" data-testid="hero-cta-secondary">
                <Button size="lg" variant="outline" className="rounded-full bg-white/10 hover:bg-white/20 border-white/30 text-white font-body gap-2 px-7 h-13 backdrop-blur-sm transition-all">
                  <Sparkles className="w-4 h-4 text-secondary" />
                  Tester mon éligibilité
                </Button>
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-4">
              {[
                { icon: CheckCircle, label: 'Coordination bout en bout' },
                { icon: Shield, label: 'Accessibilité vérifiée PMR' },
                { icon: Heart, label: 'Référent local dédié' },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-1.5 text-white/70">
                  <Icon className="w-3.5 h-3.5 text-accent" />
                  <span className="text-xs font-body">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 animate-bounce hidden md:block">
          <ChevronDown className="w-5 h-5" />
        </div>
      </section>

      {/* ═══════════════════ CHIFFRES CLÉS ═══════════════════ */}
      <section className="bg-primary text-primary-foreground py-16" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left md:divide-x md:divide-white/10">
            {[
              {
                value: '7.1', suffix: 'M', prefix: '',
                label: 'aidants informels en France', sub: 'Source : DREES 2025'
              },
              {
                value: '3', suffix: '/10', prefix: '',
                label: 'accompagnent seuls leur proche', sub: 'Étude M-CareLink · Sept. 2026'
              },
              {
                value: '100', suffix: '%', prefix: '',
                label: 'coordonné de A à Z', sub: 'Un seul interlocuteur. Zéro stress logistique.'
              },
            ].map(({ value, suffix, prefix, label, sub }) => (
              <div key={label} className="md:px-8 first:pl-0 last:pr-0">
                <p className="font-heading text-5xl font-light text-secondary mb-2">
                  <AnimatedNumber target={value} suffix={suffix} prefix={prefix} />
                </p>
                <p className="font-body font-medium text-base text-white/90 mb-1">{label}</p>
                <p className="font-body text-xs text-white/45">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ PROBLÈME ═══════════════════ */}
      <section className="py-20 md:py-28 bg-background" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-body uppercase tracking-[0.2em] text-accent mb-4">Le défi des aidants</p>
            <h2 className="font-heading font-light text-4xl md:text-5xl text-foreground leading-tight mb-5">
              Partir avec son proche.<br />
              <span className="text-muted-foreground italic">En théorie, c'est simple.</span>
            </h2>
            <p className="font-body text-base text-muted-foreground leading-relaxed">
              En France, 7,1 millions de personnes accompagnent un proche en perte d'autonomie. Organiser un départ, c'est des dizaines d'appels, de devis, d'incertitudes. M-CareLink élimine tout ça.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {painPoints.map(({ icon, title, desc }) => (
              <div key={title} className="group bg-white rounded-2xl p-7 border border-border/60 hover:border-accent/30 hover:shadow-md transition-all">
                <div className="text-3xl mb-4">{icon}</div>
                <h3 className="font-heading font-semibold text-lg mb-2 text-foreground group-hover:text-primary transition-colors">{title}</h3>
                <p className="font-body text-sm text-muted-foreground leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ SOLUTION ═══════════════════ */}
      <section className="py-20 md:py-28 bg-muted/40" data-testid="solution-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-body uppercase tracking-[0.2em] text-accent mb-4">Notre approche</p>
              <h2 className="font-heading font-light text-4xl md:text-5xl text-foreground leading-tight mb-6">
                Une seule porte d'entrée.<br />
                <span className="text-accent">Plusieurs partenaires.</span><br />
                Un séjour coordonné.
              </h2>
              <p className="font-body text-base text-muted-foreground leading-relaxed mb-8">
                M-CareLink sélectionne, coordonne et suit un réseau de partenaires certifiés — hébergeurs, transporteurs PMR, auxiliaires de vie — pour vous offrir un séjour sans couture.
              </p>
              <Link to="/comment-ca-marche" data-testid="solution-cta">
                <Button className="rounded-full bg-primary hover:bg-primary/90 text-white font-body gap-2 px-6">
                  Voir comment ça marche <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="space-y-3">
              {steps.map((step, i) => (
                <button
                  key={step.title}
                  onClick={() => setActiveStep(i)}
                  className="w-full text-left"
                  data-testid={`step-${i}`}
                >
                  <Step n={i + 1} title={step.title} desc={step.desc} active={activeStep === i} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ DESTINATIONS ═══════════════════ */}
      <section className="py-20 md:py-28 bg-background" data-testid="destinations-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-xs font-body uppercase tracking-[0.2em] text-accent mb-4">Destinations pilotes</p>
            <h2 className="font-heading font-light text-4xl md:text-5xl text-foreground leading-tight mb-4">
              Deux villes. Une même promesse.
            </h2>
            <p className="font-body text-base text-muted-foreground max-w-xl mx-auto">
              Marrakech pour la culture et les riads adaptés. Agadir pour la mer et le farniente accessible.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Marrakech */}
            <div className="group relative overflow-hidden rounded-3xl h-[420px] cursor-pointer" data-testid="marrakech-card">
              <img
                src={MARRAKECH_IMG}
                alt="Marrakech"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#30251F] via-[#30251F]/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-accent" />
                  <span className="text-xs font-body text-white/70 uppercase tracking-wider">Maroc · Terre</span>
                </div>
                <h3 className="font-heading font-medium text-3xl text-white mb-2">Marrakech</h3>
                <p className="font-body text-sm text-white/75 mb-4">Riads accessibles, médina, jardins et bien-être — la culture marocaine à votre rythme.</p>
                <div className="flex flex-wrap gap-2">
                  {['Riads PMR', 'Hammam adapté', 'Guide local'].map(tag => (
                    <span key={tag} className="text-xs bg-white/15 backdrop-blur-sm text-white rounded-full px-3 py-1 font-body">{tag}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Agadir */}
            <div className="group relative overflow-hidden rounded-3xl h-[420px] cursor-pointer" data-testid="agadir-card">
              <img
                src={AGADIR_IMG}
                alt="Agadir"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#30251F] via-[#30251F]/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-secondary" />
                  <span className="text-xs font-body text-white/70 uppercase tracking-wider">Maroc · Mer</span>
                </div>
                <h3 className="font-heading font-medium text-3xl text-white mb-2">Agadir</h3>
                <p className="font-body text-sm text-white/75 mb-4">Plages accessibles, hôtels 4★ adaptés PMR, douceur atlantique et activités inclusives.</p>
                <div className="flex flex-wrap gap-2">
                  {['Plage accessible', 'Hôtels PMR', 'Activités douces'].map(tag => (
                    <span key={tag} className="text-xs bg-white/15 backdrop-blur-sm text-white rounded-full px-3 py-1 font-body">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ FORMATS SÉJOUR ═══════════════════ */}
      <section className="py-20 md:py-24 bg-muted/40" data-testid="stays-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-xs font-body uppercase tracking-[0.2em] text-accent mb-4">Nos formules</p>
            <h2 className="font-heading font-light text-4xl md:text-5xl text-foreground leading-tight">
              L'offre qui vous correspond
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {[
              {
                name: 'Escapade',
                duration: '3 nuits',
                badge: 'Idéal pour une première fois',
                desc: 'Un week-end prolongé pour tester l\'expérience M-CareLink dans un cadre adapté et coordonné.',
                features: ['Hébergement adapté', 'Transport inclus', 'Référent local', 'Activité incluse'],
                cta: 'Demander ce séjour',
                highlight: false,
              },
              {
                name: 'Séjour Répit',
                duration: '6 à 7 nuits',
                badge: 'Le plus demandé',
                desc: 'Une semaine complète pour souffler vraiment — avec soins de continuité, activités et accompagnement 7j/7.',
                features: ['Tout inclus', 'Continuité des soins', 'Activités inclusives', 'Référent 24/7', 'Transferts aéroport'],
                cta: 'Demander ce séjour',
                highlight: true,
              },
            ].map(({ name, duration, badge, desc, features, cta, highlight }) => (
              <div
                key={name}
                className={`relative rounded-3xl p-8 border transition-all hover:-translate-y-1 ${
                  highlight
                    ? 'bg-primary text-primary-foreground border-primary shadow-xl shadow-primary/15'
                    : 'bg-white border-border/60 hover:border-accent/30 hover:shadow-md'
                }`}
                data-testid={`stay-${name.toLowerCase()}`}
              >
                {highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-accent text-white text-xs font-body font-medium px-4 py-1 rounded-full shadow-sm">{badge}</span>
                  </div>
                )}
                {!highlight && (
                  <p className="text-xs font-body text-muted-foreground mb-3">{badge}</p>
                )}
                <div className="flex items-end gap-2 mb-2">
                  <h3 className={`font-heading text-2xl font-medium ${highlight ? 'text-white' : 'text-foreground'}`}>{name}</h3>
                  <span className={`text-sm font-body mb-0.5 ${highlight ? 'text-secondary' : 'text-accent'}`}>{duration}</span>
                </div>
                <p className={`text-sm font-body leading-relaxed mb-6 ${highlight ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                  {desc}
                </p>
                <ul className="space-y-2.5 mb-8">
                  {features.map(f => (
                    <li key={f} className={`flex items-center gap-2.5 text-sm font-body ${highlight ? 'text-white/90' : 'text-foreground'}`}>
                      <CheckCircle className={`w-4 h-4 flex-shrink-0 ${highlight ? 'text-secondary' : 'text-accent'}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/demander-un-sejour">
                  <Button
                    className={`w-full rounded-full font-body ${
                      highlight
                        ? 'bg-accent hover:bg-accent/90 text-white'
                        : 'bg-primary hover:bg-primary/90 text-white'
                    }`}
                  >
                    {cta} <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ B2B ═══════════════════ */}
      <section className="py-20 md:py-24 bg-primary text-primary-foreground" data-testid="b2b-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-xs font-body uppercase tracking-[0.2em] text-secondary mb-4">Partenariats institutionnels</p>
              <h2 className="font-heading font-light text-4xl md:text-5xl text-white leading-tight mb-6">
                Vous êtes une mutuelle,<br />un CSE, une collectivité ?
              </h2>
              <p className="font-body text-base text-primary-foreground/75 leading-relaxed mb-8">
                M-CareLink construit avec vous un programme de répit sur mesure pour vos bénéficiaires aidants — financement, coordination opérationnelle, reporting. Une seule convention. Un impact durable.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/pour-les-mutuelles">
                  <Button className="rounded-full bg-accent hover:bg-accent/90 text-white font-body gap-2 px-6">
                    Déposer une demande B2B <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10 font-body gap-2 px-6">
                    <Phone className="w-4 h-4" /> Prendre rendez-vous
                  </Button>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Building2, label: 'Mutuelles & Assurances', desc: 'Programme répit aidants labellisé' },
                { icon: Users, label: 'CSE & Entreprises', desc: 'Bien-être des salariés aidants' },
                { icon: MapPin, label: 'Collectivités', desc: 'Partenariat territorial inclusif' },
                { icon: Star, label: 'Partenariats santé', desc: 'Réseau médico-social coordonné' },
              ].map(({ icon: Icon, label, desc }) => (
                <div key={label} className="bg-white/8 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:bg-white/12 transition-colors">
                  <Icon className="w-5 h-5 text-secondary mb-3" />
                  <p className="font-body font-medium text-sm text-white mb-1">{label}</p>
                  <p className="font-body text-xs text-primary-foreground/55">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ FAQ ═══════════════════ */}
      {faqItems.length > 0 && (
        <section className="py-20 md:py-24 bg-background" data-testid="faq-section">
          <div className="max-w-3xl mx-auto px-6 sm:px-8">
            <div className="text-center mb-12">
              <p className="text-xs font-body uppercase tracking-[0.2em] text-accent mb-4">Questions fréquentes</p>
              <h2 className="font-heading font-light text-4xl md:text-5xl text-foreground leading-tight">
                Tout ce que vous voulez savoir
              </h2>
            </div>
            <div className="bg-white rounded-3xl border border-border/60 shadow-sm px-8 py-2 divide-y divide-border/40">
              {faqItems.map(item => (
                <FAQItem key={item.id} q={item.question} a={item.answer} />
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/faq">
                <Button variant="outline" className="rounded-full font-body border-border/60">
                  Voir toutes les questions <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ═══════════════════ MAP TEASER ═══════════════════ */}
      {!user && (
        <section
          className="relative overflow-hidden"
          style={{ height: 520 }}
          data-testid="map-teaser-section"
          aria-label="Accès membre - Carte des partenaires"
        >
          {/* ── Fond aérien flouté ── */}
          <div
            aria-hidden="true"
            className="absolute inset-0 scale-110"
            style={{
              backgroundImage: `url('https://images.pexels.com/photos/30208104/pexels-photo-30208104.jpeg?auto=compress&cs=tinysrgb&w=1400&q=80')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center 40%',
              filter: 'blur(6px) brightness(0.4) saturate(0.75)',
            }}
          />

          {/* ── Overlay dégradé ── */}
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{ background: 'linear-gradient(160deg, #30251Fcc 0%, #5A3B31aa 50%, #30251Fcc 100%)' }}
          />

          {/* ── Marqueurs carte animés (déco) ── */}
          {[
            { top: '20%', left: '18%', color: '#F47B20', delay: '0s' },
            { top: '55%', left: '28%', color: '#F47B20', delay: '0.8s' },
            { top: '30%', right: '20%', color: '#5A3B31', delay: '1.4s' },
            { top: '65%', right: '15%', color: '#5A3B31', delay: '0.3s' },
            { top: '45%', left: '8%', color: '#FFB343', delay: '1.9s' },
          ].map((pin, i) => (
            <div
              key={i}
              aria-hidden="true"
              className="absolute hidden lg:flex items-center justify-center"
              style={{ top: pin.top, left: pin.left, right: pin.right, zIndex: 5 }}
            >
              <span
                className="absolute w-7 h-7 rounded-full animate-ping"
                style={{ background: pin.color, opacity: 0.25, animationDelay: pin.delay }}
              />
              <span className="relative w-3 h-3 rounded-full border-2 border-white" style={{ background: pin.color }} />
            </div>
          ))}

          {/* ── Cartes partenaires floutées (déco gauche) ── */}
          <div
            aria-hidden="true"
            className="absolute left-8 top-1/2 -translate-y-1/2 hidden xl:block space-y-3"
            style={{ filter: 'blur(2.5px)', opacity: 0.45, zIndex: 5 }}
          >
            <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-4 w-52 border border-white/10">
              <div className="flex items-center gap-2 mb-2.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#F47B20] flex-shrink-0" />
                <div className="h-3 bg-white/50 rounded-full w-28" />
              </div>
              <div className="h-2 bg-white/25 rounded-full w-full mb-1.5" />
              <div className="h-2 bg-white/20 rounded-full w-3/4 mb-3" />
              <div className="flex gap-1">
                {[1,2,3,4,5].map(j => <span key={j} className="w-3 h-3 rounded-full bg-[#F47B20]/70" />)}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 w-52 border border-white/8">
              <div className="flex items-center gap-2 mb-2.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#F47B20] flex-shrink-0" />
                <div className="h-3 bg-white/40 rounded-full w-20" />
              </div>
              <div className="h-2 bg-white/20 rounded-full w-full mb-1.5" />
              <div className="h-2 bg-white/15 rounded-full w-2/3" />
            </div>
          </div>

          {/* ── Cartes partenaires floutées (déco droite) ── */}
          <div
            aria-hidden="true"
            className="absolute right-8 top-1/2 -translate-y-1/2 hidden xl:block space-y-3"
            style={{ filter: 'blur(2.5px)', opacity: 0.4, zIndex: 5 }}
          >
            <div className="bg-white/12 backdrop-blur-sm rounded-2xl p-4 w-52 border border-white/8">
              <div className="flex items-center gap-2 mb-2.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FFB343] flex-shrink-0" />
                <div className="h-3 bg-white/45 rounded-full w-24" />
              </div>
              <div className="h-2 bg-white/20 rounded-full w-full mb-1.5" />
              <div className="h-2 bg-white/15 rounded-full w-4/5 mb-3" />
              <div className="flex gap-1">
                {[1,2,3,4].map(j => <span key={j} className="w-3 h-3 rounded-full bg-[#FFB343]/60" />)}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 w-52 border border-white/8">
              <div className="flex items-center gap-2 mb-2.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FFB343] flex-shrink-0" />
                <div className="h-3 bg-white/35 rounded-full w-16" />
              </div>
              <div className="h-2 bg-white/15 rounded-full w-full mb-1.5" />
              <div className="h-2 bg-white/12 rounded-full w-3/4" />
            </div>
          </div>

          {/* ── Contenu central ── */}
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">

            {/* Icône cadenas avec halo animé */}
            <div className="relative mb-7">
              <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center shadow-xl shadow-black/30">
                <Lock className="w-7 h-7 text-accent" aria-hidden="true" />
              </div>
              <div
                className="absolute inset-0 rounded-full animate-ping bg-accent/20"
                style={{ animationDuration: '2.6s' }}
                aria-hidden="true"
              />
            </div>

            {/* Badge exclusivité */}
            <div className="inline-flex items-center gap-2 bg-accent/20 backdrop-blur-sm border border-accent/30 rounded-full px-4 py-1.5 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-accent" aria-hidden="true" />
              <span className="text-xs font-body text-white/90 tracking-wide font-medium">Accès membre exclusif</span>
            </div>

            {/* Titre */}
            <h2 className="font-heading font-light text-4xl md:text-5xl text-white leading-tight mb-4">
              6 partenaires certifiés<br className="hidden sm:block" /> sur la carte
            </h2>

            {/* Sous-titre */}
            <p className="font-body text-sm md:text-base text-white/65 max-w-sm leading-relaxed mb-7">
              Visualisez géographiquement les établissements M-CareLink à{' '}
              <span className="text-secondary font-medium">Marrakech</span> et{' '}
              <span className="text-secondary font-medium">Agadir</span> — spécialités, notes et contacts.
            </p>

            {/* Badges villes */}
            <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-4 py-2">
                <span className="w-2 h-2 rounded-full bg-[#F47B20]" aria-hidden="true" />
                <span className="text-xs font-body text-white/80">Marrakech · 3 partenaires</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-4 py-2">
                <span className="w-2 h-2 rounded-full bg-[#FFB343]" aria-hidden="true" />
                <span className="text-xs font-body text-white/80">Agadir · 3 partenaires</span>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/connexion" data-testid="map-teaser-login-btn">
                <Button
                  size="lg"
                  className="rounded-full bg-accent hover:bg-accent/90 text-white font-body gap-2 px-7 h-12 shadow-lg shadow-accent/30 transition-all hover:scale-105"
                >
                  <MapPin className="w-4 h-4" aria-hidden="true" />
                  Se connecter pour accéder
                </Button>
              </Link>
              <Link to="/connexion" data-testid="map-teaser-register-btn">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full bg-white/10 hover:bg-white/20 border-white/30 text-white font-body px-7 h-12 backdrop-blur-sm transition-all"
                >
                  Créer un compte gratuit
                </Button>
              </Link>
            </div>

            <p className="text-[11px] font-body text-white/35 mt-5">
              Inscription gratuite · Accès immédiat · Aucune carte bancaire requise
            </p>
          </div>
        </section>
      )}

      {/* ═══════════════════ CTA FINAL ═══════════════════ */}
      <section className="py-24 md:py-32 bg-accent overflow-hidden relative" data-testid="final-cta-section">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #fff 0%, transparent 60%), radial-gradient(circle at 80% 50%, #fff 0%, transparent 60%)' }}
        />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs font-body uppercase tracking-[0.2em] text-white/70 mb-5">Lancez-vous</p>
          <h2 className="font-heading font-light text-4xl md:text-6xl text-white leading-tight mb-6">
            Prêt à souffler enfin ?
          </h2>
          <p className="font-body text-base text-white/80 mb-10 max-w-xl mx-auto leading-relaxed">
            Décrivez votre situation en 5 minutes. Notre équipe analyse vos besoins et revient vers vous sous 48h avec une proposition personnalisée.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/demander-un-sejour" data-testid="final-cta-btn">
              <Button size="lg" className="rounded-full bg-white text-accent hover:bg-white/90 font-body font-semibold gap-2 px-8 h-13 shadow-lg transition-all hover:scale-105">
                Déposer ma demande <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="rounded-full bg-transparent border-white/40 text-white hover:bg-white/10 font-body gap-2 px-8 h-13">
                <Phone className="w-4 h-4" /> Nous contacter
              </Button>
            </Link>
          </div>
          <p className="text-xs text-white/50 font-body mt-6">
            Réponse garantie sous 48h · Service confidentiel · 100% gratuit pour vous
          </p>
        </div>
      </section>

    </div>
  );
}
